import { Intent, useFullTheme } from '~/design';
import { HelpText } from '../HelpText';
import { useId } from '~/utils';
import { Flex } from '../Flex';

interface SubtleNoticeProps {
  // We discussed this with Hamed and we decided "important" is not needed,
  // even though it's supported by [GitHub](https://github.com/orgs/community/discussions/16925)
  // because it's kind of redundant with "important"
  importance: 'note' | 'tip' | 'warning' | 'caution';
  children: React.ReactNode;
}

/**
 * SubtleNotice is a lower saliency version of Notice.
 *
 * It's mostly meant for use by CX inside the Markdown component.  It's meant
 * to look different from the higher saliency Notice, so users can tell the
 * difference between notices provided by CX and notices provided by the
 * system
 */
export function SubtleNotice({ importance, children }: SubtleNoticeProps) {
  const theme = useFullTheme();
  const labelMapping: Record<SubtleNoticeProps['importance'], string> = {
    note: 'Note',
    tip: 'Tip',
    caution: 'Caution',
    warning: 'Warning',
  };
  const label = labelMapping[importance];

  const intentMapping: Record<SubtleNoticeProps['importance'], Intent> = {
    caution: 'negative',
    warning: 'warning',
    note: 'primary',
    tip: 'positive',
  };
  const intent = intentMapping[importance];

  const leftBarColorMapping: Record<SubtleNoticeProps['importance'], string> = {
    caution: theme.colors.iconNegative,
    warning: theme.colors.iconWarning,
    note: theme.colors.iconPrimary,
    tip: theme.colors.iconPositive,
  };
  const leftBarColor = leftBarColorMapping[importance];
  const labelId = useId();

  return (
    <div
      css={theme => ({
        display: 'flex',
        flexDirection: 'row',
        gap: theme.spacing[4],
      })}
      role="region"
      aria-labelledby={labelId}
    >
      <div
        css={{
          width: '4px',
          backgroundColor: leftBarColor,
          flexGrow: 0,
          flexShrink: 0,
        }}
        contentEditable="false"
        suppressContentEditableWarning
      ></div>
      <div
        css={{
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing[2],
        }}
      >
        <span contentEditable="false" suppressContentEditableWarning>
          <HelpText id={labelId} intent={intent} showIcon>
            {label}
          </HelpText>
        </span>
        <Flex direction="column" gap={4}>
          {children}
        </Flex>
      </div>
    </div>
  );
}
